import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import {
  startIntakeFromFaxItem,
  startIntakeFromFaxItems,
  type FaxItem,
  type FaxItemIntakeStarted,
  type FaxItemRef,
  type FaxItemsIntakeStarted,
} from '../../services/faxItems';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { displayName } from './faxItemDisplay';

interface CommonProps {
  open: boolean;
  /** True while the loaded row is not yet the one in the URL. */
  disabled?: boolean;
  onClose: () => void;
  onError: (message: string) => void;
}

interface OneFaxProps extends CommonProps {
  /** The fax as loaded, for its name and pages. */
  item: FaxItem;
  /** The row the intake is started from: the one in the URL. */
  itemRef: FaxItemRef;
  items?: never;
  onStarted: (started: FaxItemIntakeStarted) => void;
}

interface SeveralFaxesProps extends CommonProps {
  /** The faxes chosen in the list, in the order they are sent. */
  items: FaxItem[];
  item?: never;
  itemRef?: never;
  onStarted: (started: FaxItemsIntakeStarted) => void;
}

export type CreateIntakeDialogProps = OneFaxProps | SeveralFaxesProps;

function isPdf(file: File): boolean {
  return (
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  );
}

function pageLabel(item: FaxItem): string {
  if (item.page_count === null) return '';
  return item.page_count === 1 ? '1 page' : `${item.page_count} pages`;
}

/**
 * Start one intake from one fax or several, with any other PDFs it needs.
 *
 * The faxes are always first, in the order given, and cannot be taken off:
 * starting from them is what this dialog is for. Starting a second intake
 * from the same fax is allowed, with a warning, because the same pages can
 * legitimately start two.
 */
export default function CreateIntakeDialog(props: CreateIntakeDialogProps) {
  const { open, disabled = false, onClose, onError } = props;
  const faxes = props.items ?? [props.item];
  const alreadyStarted = faxes.filter(fax => fax.intake_id).length;
  const [added, setAdded] = useState<File[]>([]);
  const [starting, setStarting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const browse = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) setAdded([]);
  }, [open]);

  const take = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const pdfs = Array.from(files).filter(isPdf);
      if (pdfs.length !== files.length) {
        onError('Only PDF files can be added to an intake.');
      }
      if (pdfs.length > 0) setAdded(previous => [...previous, ...pdfs]);
    },
    [onError]
  );

  const start = async () => {
    setStarting(true);
    try {
      if (props.items) {
        props.onStarted(await startIntakeFromFaxItems(props.items, added));
      } else {
        props.onStarted(
          await startIntakeFromFaxItem(
            props.itemRef.kind,
            props.itemRef.id,
            added
          )
        );
      }
    } catch (error) {
      // The added files stay, so a retry does not mean finding them again.
      console.error('Failed to start the intake', error);
      onError(getBusinessErrorMessage(error) ?? 'Could not start the intake.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => !next && !starting && onClose()}>
      <DialogContent className='flex max-h-[90vh] max-w-xl flex-col'>
        <DialogHeader>
          <DialogTitle>Create intake</DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
          {props.item?.intake_id && (
            <p className='text-sm text-muted-foreground'>
              A referral was already started from this fax.{' '}
              <Link
                className='font-medium text-primary underline underline-offset-2'
                to={`/intake/${props.item.intake_id}`}
              >
                Open referral
              </Link>
            </p>
          )}
          {props.items && alreadyStarted > 0 && (
            <p className='text-sm text-muted-foreground'>
              {alreadyStarted === 1
                ? 'A referral was already started from one of these faxes.'
                : `Referrals were already started from ${alreadyStarted} of these faxes.`}
            </p>
          )}

          <div className='space-y-1.5'>
            <p className='text-sm font-medium'>Documents</p>
            <ul className='space-y-1.5'>
              {faxes.map(fax => (
                <li
                  key={`${fax.kind}-${fax.id}`}
                  className='flex items-center gap-2 rounded border px-3 py-2 text-sm'
                >
                  <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='min-w-0 flex-1 truncate'>
                    {displayName(fax)}
                  </span>
                  {pageLabel(fax) && (
                    <span className='whitespace-nowrap text-xs text-muted-foreground'>
                      {pageLabel(fax)}
                    </span>
                  )}
                  <Badge variant='secondary' className='whitespace-nowrap'>
                    Fax
                  </Badge>
                </li>
              ))}
              {added.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className='flex items-center gap-2 rounded border px-3 py-2 text-sm'
                >
                  <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='min-w-0 flex-1 truncate'>{file.name}</span>
                  <Button
                    variant='ghost'
                    size='sm'
                    aria-label={`Remove ${file.name}`}
                    disabled={starting}
                    onClick={() =>
                      setAdded(previous =>
                        previous.filter((_, at) => at !== index)
                      )
                    }
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          <div
            className={`rounded-md border-2 border-dashed p-6 text-center transition-colors ${
              dragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25'
            }`}
            onDragOver={event => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={event => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={event => {
              event.preventDefault();
              setDragging(false);
              take(event.dataTransfer.files);
            }}
          >
            <Upload className='mx-auto mb-2 h-8 w-8 text-muted-foreground' />
            <p className='mb-3 text-sm text-muted-foreground'>
              Drop PDFs here, or browse
            </p>
            <input
              ref={browse}
              type='file'
              accept='application/pdf'
              multiple
              className='hidden'
              aria-label='Add PDF documents'
              onChange={event => {
                take(event.target.files);
                event.target.value = '';
              }}
            />
            <Button
              variant='outline'
              type='button'
              disabled={starting}
              onClick={() => browse.current?.click()}
            >
              Add more PDFs
            </Button>
          </div>
        </div>

        <div className='flex shrink-0 flex-wrap justify-end gap-2 pt-2'>
          <Button
            variant='outline'
            type='button'
            disabled={starting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type='button'
            disabled={starting || disabled}
            onClick={() => void start()}
          >
            {starting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Start intake
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
